<template>
    <div v-show="visible">
        <label>
            {{ t(displayName) }}
        </label>
        <multiselect
            :placeholder="dynamicSearch ? t('Type to search') : t('Select')"
            :options="availableValues"
            :multiple="isMultiple"
            :limit="15"
            :options-limit="1000"
            :limit-text="limitText"
            :close-on-select="closeOnSelect"
            :clear-on-select="false"
            track-by="id"
            label="name"
            :group-values="group ? 'rows' : ''"
            :internalSearch="!group"
            group-label="name"
            :group-select="group"
            :loading="loading"
            v-model="value"
            @open="onOpen"
            @input="onInput"
            @search-change="searchChange"
        >
            <div slot="noOptions">{{ t('List is empty') }}</div>
        </multiselect>
    </div>
</template>

<script>

import Multiselect from '../Multiselect';
import State from "@/Models/State.ts";
import ValueChecker from "@/services/ValueChecker";

export default {
    components: {Multiselect},
    props: {
        config: {
            type: Object,
            default: () => {
                return {
                    name: null,
                    group: false,
                    value: [],
                    loadUrl: null,
                    display: true,
                    position: null,
                    isMultiple: null,
                    displayName: null,
                    transLabel: false,
                    closeOnSelect: false,
                    dynamicSearch: false,
                };
            }
        },
        eventBus: {
            Type: Object,
        },
        routeParams: {
            Type: Object
        },
        queryParams: {
            Type: Object
        },
        onChange: {
            Type: Function,
        },
        onDefaultValueSet: {
            Type: Function,
        },
    },
    computed: {
        closeOnSelect() {
            return ValueChecker.isUndefined(this.config.closeOnSelect) ? false : this.config.closeOnSelect;
        }
    },
    data() {
        return {
            displayName: null,
            visible: true,
            isMultiple: false,
            group: false,
            value: null,
            availableValues: [],
            copyAvailableValues: [],
            loading: false,
            name: null,
            filterState: new State(),
            dynamicSearch: false,
            interval: null,
            limitText (count) {
                return this.$trans('and %count% other items', {count: count});
            },
        };
    },
    beforeDestroy() {
        this.unmountEvents();
    },
    async mounted() {
        this.init();
        this.mountEvents();
        this.setFilterState(this.routeParams)
            .setFilterState(this.queryParams);
        await this.setDefaultValue(this.queryParams[this.name] || this.config.value);
    },
    methods: {
        unmountEvents() {
            this.$parent.$off(`${this.name}-set-value`);
            this.$parent.$off(`filter-set`);
            this.$parent.$off(`reset-state`);
        },
        mountEvents() {
            this.$parent.$on(`${this.name}-set-value`, (filterState) => {
                this.setValue(filterState);
            });
            this.$parent.$on('filter-set', (filterName, filterState) => {
                if (this.name === filterName) {
                    return;
                }

                this.setFilterState(filterState);
                this.availableValues = [];
            });
            this.$parent.$on('reset-state', (filterState) => {
                this.resetFilterState(filterState);
            });
        },
        init() {
            this.displayName = this.config.displayName;
            this.visible = this.config.visible;
            this.isMultiple = this.config.isMultiple;
            this.dynamicSearch = this.config.dynamicSearch;
            this.name = this.config.name;
            this.group = this.config.group;
        },
        async getAvailableValues(params) {
            const url = this.config['loadUrl'];

            return await this.$http['get'](url, {params: params});
        },
        async onOpen() {
            if (!this.dynamicSearch && this.availableValues.length === 0) {
                await this.fetchAvailableValues(this.filterState.value);
            }
        },
        async fetchAvailableValues(params) {
            this.loading = true;
            let response = await this.getAvailableValues(params);

            if (this.config.transLabel) {
                this.availableValues = this.transOptionLabel(response.data.data);
            } else {
                this.availableValues = response.data.data;
            }

            this.copyAvailableValues = response.data.data;
            this.loading = false;
        },
        transOptionLabel(data) {
            return data.map(item => {
                if (this.group) {
                    return {
                        name: this.$trans(item.name),
                        rows: item.rows.map(row => {
                            return {
                                id: row.id,
                                name: this.$trans(row.name)
                            };
                        })
                    };
                }

                return {
                    id: item.id,
                    name: this.$trans(item.name)
                };
            });
        },
        onInput() {
            let value = [];

            if (this.isMultiple) {
                value = this.value.map(item => item.id);
            } else if (ValueChecker.isObject(this.value)) {
                value.push(this.value.id);
            }

            this.onChange(this.name, value);
        },
        setFilterState(data) {
            this.filterState.value = data;

            return this;
        },
        resetFilterState(state) {
            this.filterState.resetState(state);
        },
        searchWithGroupOption(query) {
            this.availableValues = this.copyAvailableValues
                .map(group => ({
                    ...group,
                    rows: group.rows.filter(item =>
                        item.name.toLowerCase().includes(query) || group.name.toLowerCase().includes(query)
                    )
                }))
                .filter(group =>
                    group.name.toLowerCase().includes(query) || group.rows.length
                )
            ;
        },
        async searchChange(searchQuery) {
            let query = searchQuery.toLowerCase().trim();

            if (this.dynamicSearch) {
                return this.dynamicSearchCallback(query);
            }

            if (this.group) {
                return this.searchWithGroupOption(query);
            }
        },
        async dynamicSearchCallback(query) {
            clearInterval(this.interval);

            if ('' === query.trim()) {
                return;
            }

            this.interval = setTimeout(async () => {
                let params = Object.assign({query: query}, this.filterState.value);
                await this.fetchAvailableValues(params);
            }, 1000);
        },
        async setDefaultValue(value) {
            if (!Array.isArray(value) || 0 === value.length) {
                return;
            }

            await this.setValue(value, this.filterState.value);
            let scalarValue = this.value.map(item => item.id);
            this.onDefaultValueSet(this.name, scalarValue);
        },
        async setValue(value, params) {
            if (!Array.isArray(value) || 0 === value.length) {
                this.value = value;

                return;
            }

            this.loading = true;
            params = Object.assign({id: value}, params || {});
            let response = await this.getAvailableValues(params);

            if (this.group) {
                let valueToSet = [];
                response.data.data.map(group => {
                    valueToSet = valueToSet.concat(group.rows.filter(item => value.includes(item.id)));
                });
                this.value = valueToSet;
            } else {
                this.value = response.data.data.filter(item => value.includes(item.id));
            }

            let scalarValue = this.value.map(item => item.id);
            this.setFilterState({[this.name]: scalarValue});
            this.loading = false;
        }
    }
}

</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// SelectComponent.vue?000f4810